import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Container,
    FormGroup,
    Label,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText
} from "reactstrap";
import { authenticatedFetch } from "../../services/authService";
import rest from "../../API/api";

const PreviewWaiver = () => {
    const { id } = useParams();
    const [waiver, setWaiver] = useState(null);
    const [loading, setLoading] = useState(true);
    const history = useHistory();

    useEffect(() => {
        if (id) {
            loadWaiver();
        }
    }, [id]);

    const loadWaiver = async () => {
        try {
            const response = await authenticatedFetch(`${rest.waivers}/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setWaiver(data);
            } else {
                alert("Waiver not found");
                history.push("/admin/managewaivers");
            }
            setLoading(false);
        } catch (error) {
            console.error('Error loading waiver:', error);
            alert("Error loading waiver");
            history.push("/admin/managewaivers");
        }
    };

    const renderWaiverSection = (header, text) => {
        if (!header && !text) return null;

        return (
            <div className="mb-5">
                {header && (
                    <h4 className="text-center mb-4" style={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        fontWeight: '700',
                        fontSize: '1.5rem',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        color: '#fff',
                        borderBottom: '2px solid #1d8cf8',
                        paddingBottom: '10px',
                        marginBottom: '20px'
                    }}>
                        {header}
                    </h4>
                )}
                {text && (
                    <p style={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.8',
                        textAlign: 'justify',
                        fontSize: '1.05rem',
                        color: '#fff',
                        marginBottom: '2rem',
                        wordSpacing: 'normal',
                        wordWrap: 'break-word',
                        hyphens: 'auto'
                    }}>
                        {text}
                    </p>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="content">
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8}>
                            <Card>
                                <CardBody>
                                    <p className="text-center">Loading waiver preview...</p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="content">
            <style>
                {`
                    .preview-input-group {
                        border: 2px solid #2c3e50 !important;
                        border-radius: 4px !important;
                        background-color: #f5f5f5 !important;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                    }
                    .preview-input-group .form-control {
                        border: none !important;
                        background-color: #f5f5f5 !important;
                        color: #999 !important;
                        cursor: not-allowed !important;
                    }
                    .preview-input-group .input-group-text {
                        border: none !important;
                        background-color: #f5f5f5 !important;
                        color: #2c3e50 !important;
                    }
                    .preview-signature-box {
                        border: 2px solid #2b3553;
                        borderRadius: 5px;
                        backgroundColor: #f5f5f5;
                        height: 200px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #999;
                        font-style: italic;
                    }
                `}
            </style>
            <Container>
                {/* Back Button */}
                <Row className="justify-content-center mb-3">
                    <Col md={10} className="text-right">
                        <Button
                            color="secondary"
                            onClick={() => history.push("/admin/managewaivers")}
                        >
                            <i className="tim-icons icon-minimal-left"/> Back to Manage Waivers
                        </Button>
                    </Col>
                </Row>

                {/* Preview Badge */}
                <Row className="justify-content-center mb-3">
                    <Col md={10} className="text-center">
                        <div style={{
                            display: 'inline-block',
                            padding: '10px 20px',
                            backgroundColor: '#f39c12',
                            color: '#fff',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                        }}>
                            <i className="tim-icons icon-zoom-split"/> PREVIEW MODE - Form fields are read-only
                        </div>
                    </Col>
                </Row>

                {/* Title and Version */}
                <Row className="justify-content-center mb-4">
                    <Col md={10} className="text-center">
                        <h2 style={{ color: '#fff', marginBottom: '10px' }}>{waiver.Title}</h2>
                        {waiver.Version && <p style={{ color: '#aaa', fontSize: '1.1rem' }}>Version {waiver.Version}</p>}
                    </Col>
                </Row>

                {/* Image */}
                {waiver.ImageUrl && (
                    <Row className="justify-content-center mb-4">
                        <Col md={10}>
                            <div style={{
                                padding: '30px',
                                backgroundColor: 'white',
                                border: '2px solid #2c3e50',
                                borderRadius: '4px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                textAlign: 'center'
                            }}>
                                <img src={waiver.ImageUrl} alt="Waiver Header" style={{maxWidth: '100%', maxHeight: '300px'}} />
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Waiver Content */}
                <Row className="justify-content-center mb-4">
                    <Col md={10}>
                        {renderWaiverSection(waiver.Header1, waiver.WaiverText1)}
                        {renderWaiverSection(waiver.Header2, waiver.WaiverText2)}
                        {renderWaiverSection(waiver.Header3, waiver.WaiverText3)}
                    </Col>
                </Row>

                <Row className="justify-content-center">
                    <Col md={10}>
                        <Card>
                            <CardBody>

                                {/* Participant Information Section */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '30px',
                                            backgroundColor: 'white',
                                            border: '2px solid #2c3e50',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h4 style={{ color: '#000', marginBottom: '25px', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>Participant Information</h4>
                                            <Row>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#000' }}>First Name <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="First Name" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#000' }}>Middle Name</Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="Middle Name" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#000' }}>Last Name <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="Last Name" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#000' }}>Email <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-email-85"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="email" disabled placeholder="email@example.com" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#000' }}>Phone <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-mobile"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="tel" disabled placeholder="(555) 555-5555" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#000' }}>Date of Birth <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-calendar-60"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="date" disabled />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Address Section */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '30px',
                                            backgroundColor: '#e8f4f8',
                                            border: '2px solid #1565c0',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h4 style={{ color: '#1565c0', marginBottom: '25px', borderBottom: '2px solid #1565c0', paddingBottom: '10px' }}>Address</h4>
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#0d47a1' }}>Address Line 1</Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="Street Address" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#0d47a1' }}>Address Line 2</Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="Apt, Suite, etc." />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#0d47a1' }}>City</Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="City" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#0d47a1' }}>State</Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="State" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#0d47a1' }}>Zip Code</Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-square-pin"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="Zip Code" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Emergency Contact Section */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '30px',
                                            backgroundColor: '#fff9e6',
                                            border: '2px solid #f57c00',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h4 style={{ color: '#e65100', marginBottom: '25px', borderBottom: '2px solid #f57c00', paddingBottom: '10px' }}>Emergency Contact</h4>
                                            <Row>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#e65100' }}>First Name <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-badge"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="First Name" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#e65100' }}>Last Name <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-badge"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="Last Name" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#e65100' }}>Phone <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-mobile"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="tel" disabled placeholder="(555) 555-5555" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Parent/Guardian Section - Always show as example */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '30px',
                                            backgroundColor: '#f3e5f5',
                                            border: '2px solid #7b1fa2',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h4 style={{ color: '#6a1b9a', marginBottom: '25px', borderBottom: '2px solid #7b1fa2', paddingBottom: '10px' }}>
                                                <i className="tim-icons icon-alert-circle-exc"/> Parent/Guardian Information (Required for Minors)
                                            </h4>
                                            <Row>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#6a1b9a' }}>First Name <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="First Name" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#6a1b9a' }}>Last Name <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-single-02"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="text" disabled placeholder="Last Name" />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label style={{ color: '#6a1b9a' }}>Date of Birth <span className="text-danger">*</span></Label>
                                                        <InputGroup className="preview-input-group">
                                                            <InputGroupAddon addonType="prepend">
                                                                <InputGroupText><i className="tim-icons icon-calendar-60"/></InputGroupText>
                                                            </InputGroupAddon>
                                                            <Input type="date" disabled />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <h5 className="title mt-4" style={{ color: '#6a1b9a' }}>Parent / Guardian Acknowledgment (Required for Minors)</h5>
                                            <p style={{ color: '#6a1b9a', fontSize: '0.95rem', marginBottom: '15px', lineHeight: '1.6' }}>
                                                I am the legal parent/guardian of the minor participant. I acknowledge that I am waiving and releasing the minor's rights and my own rights to the fullest extent permitted by Colorado law. I agree to all terms of this Agreement on behalf of the minor.
                                            </p>
                                            <h6 style={{ color: '#6a1b9a' }}>Parent/Guardian Signature <span className="text-danger">*</span></h6>
                                            <Row>
                                                <Col md={12}>
                                                    <div className="preview-signature-box">
                                                        Signature Area (Disabled in Preview)
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>

                                <h5 className="title mt-4">Participant (Age 18 or older)</h5>
                                <p style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '15px', lineHeight: '1.6' }}>
                                    I have read and understand this Agreement. I sign voluntarily.
                                </p>
                                <h6 style={{ color: '#fff' }}>Participant Signature <span className="text-danger">*</span></h6>
                                <Row>
                                    <Col md={12}>
                                        <div className="preview-signature-box">
                                            Signature Area (Disabled in Preview)
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="mt-5">
                                    <Col md={12}>
                                        <div style={{
                                            padding: '20px',
                                            backgroundColor: 'rgba(29, 140, 248, 0.1)',
                                            border: '2px solid #1d8cf8',
                                            borderRadius: '4px',
                                            marginBottom: '20px'
                                        }}>
                                            <h5 style={{ color: '#1d8cf8', marginBottom: '10px' }}>Colorado Inherent Risk Acknowledgment</h5>
                                            <p style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '0', lineHeight: '1.6' }}>
                                                I acknowledge that under Colorado law, participants in recreational and athletic activities assume the inherent risks of such activities. This Agreement is intended to be interpreted and enforced consistent with that principle and Colorado public policy.
                                            </p>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col md={12}>
                                        <FormGroup check style={{ opacity: 0.6 }}>
                                            <Label check style={{ color: '#fff' }}>
                                                <Input type="checkbox" disabled />
                                                <span className="form-check-sign"/>
                                                I consent to the use of electronic signatures and agree that my electronic signature is the legal equivalent of my manual signature <span className="text-danger">*</span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <FormGroup check style={{ opacity: 0.6 }}>
                                            <Label check style={{ color: '#fff' }}>
                                                <Input type="checkbox" disabled />
                                                <span className="form-check-sign"/>
                                                I would like to receive email updates and communications
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col md={12} className="text-center">
                                        <Button
                                            size="lg"
                                            disabled
                                            style={{
                                                backgroundColor: '#999',
                                                borderColor: '#999',
                                                color: 'white',
                                                padding: '12px 40px',
                                                fontWeight: 'bold',
                                                cursor: 'not-allowed'
                                            }}
                                        >
                                            Submit Waiver (Disabled in Preview)
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default PreviewWaiver;
